import React from "react";
import { Badge, BadgeVariant } from "../../badge";
import { OverflowDisplayTooltip } from "../../Tooltip/OverflowDisplayTooltip";
import { BasicTooltip } from "../../Tooltip";

export enum CardTagsSeparator {
  DOT = "•",
  PIPE = "|",
}

interface CardTagsProps {
  tags: string[];
  maxVisibleTags?: number;
  useBadge?: boolean;
  separator?: CardTagsSeparator;
  emptyText?: string;
  shouldShowEmptyText?: boolean;
}

export const CardTags = ({
  tags,
  maxVisibleTags = 3,
  useBadge = true,
  separator,
  emptyText,
  shouldShowEmptyText = false,
}: CardTagsProps) => {
  if (tags.length === 0) {
    if (emptyText) {
      return (
        <div className="mt-2 flex min-w-0 max-w-full items-center gap-1 overflow-hidden">
          <div className="inline-flex text-xs font-medium text-gray-400">
            {emptyText}
          </div>
        </div>
      );
    }

    if (shouldShowEmptyText) {
      return (
        <div className="mt-2 flex min-w-0 max-w-full items-center gap-1 overflow-hidden">
          <div className="inline-flex h-[22px] text-xs font-medium text-gray-400" />
        </div>
      );
    }

    return null;
  }

  const displayTags = tags.slice(0, maxVisibleTags);
  const remainingTags = tags.slice(maxVisibleTags);

  return (
    <div className="mt-2 flex min-w-0 max-w-full items-center gap-1 overflow-hidden">
      {displayTags.map((tag: string, idx: number) => (
        <div
          key={idx}
          className={`flex min-w-0 shrink items-center gap-x-1.5 text-xs text-gray-500 ${
            !useBadge ? "max-w-full truncate" : ""
          }`}
        >
          {!useBadge ? (
            <OverflowDisplayTooltip content={tag}>
              <span className="truncate">{tag}</span>
            </OverflowDisplayTooltip>
          ) : (
            <Badge
              variant={BadgeVariant.SECONDARY}
              title={tag}
              className="min-w-0 max-w-full shrink truncate"
            >
              <OverflowDisplayTooltip content={tag}>
                <span className="truncate">{tag}</span>
              </OverflowDisplayTooltip>
            </Badge>
          )}
          {idx < displayTags.length - 1 && separator && (
            <span className="shrink-0">{separator}</span>
          )}
        </div>
      ))}
      {remainingTags.length > 0 && (
        <div className="flex min-w-0 shrink-0 items-center gap-x-1.5">
          <BasicTooltip content={remainingTags.join(", ")}>
            <span className="inline-block whitespace-nowrap px-1 text-xs text-gray-400">
              +{remainingTags.length} more
            </span>
          </BasicTooltip>
        </div>
      )}
    </div>
  );
};
